import type { ReviewProgressItem, ReviewProgressStage } from "@compliance-sage/contracts";
import type { SourceGroupType } from "../complianceScope/utils";
import { GROUP_ORDER, groupNodeId } from "./graphModel";

export const CHANNEL_NODE_ID = "channel";
export const CATEGORY_NODE_ID = "category";
export const AI_REVIEW_NODE_ID = "ai";
export const DECISION_NODE_ID = "decision";

export const CHANNEL_TO_CATEGORY_EDGE_ID = `${CHANNEL_NODE_ID}->${CATEGORY_NODE_ID}`;
export const AI_TO_DECISION_EDGE_ID = `${AI_REVIEW_NODE_ID}->${DECISION_NODE_ID}`;

export function channelToGroupEdgeId(groupType: SourceGroupType): string {
  return `${CHANNEL_NODE_ID}->${groupNodeId(groupType)}`;
}

export function categoryToGroupEdgeId(groupType: SourceGroupType): string {
  return `${CATEGORY_NODE_ID}->${groupNodeId(groupType)}`;
}

export function groupToAiEdgeId(groupType: SourceGroupType): string {
  return `${groupNodeId(groupType)}->${AI_REVIEW_NODE_ID}`;
}

export type DecisionState = "pending" | "success" | "failed";

export interface LiveHighlight {
  activeNodeIds: Set<string>;
  activeEdgeIds: Set<string>;
  decisionState: DecisionState;
}

/**
 * Maps a review's current analysis stage onto the Sage Workflow slice:
 * Channel -> Category -> used source groups -> AI Review -> Decision.
 * DETERMINISTIC_CHECKS only touches Custom Rules + Forbidden Expressions;
 * AI_REVIEW unifies the used groups into one chat request.
 */
export function stageHighlight(
  stage: ReviewProgressStage,
  jobStatus: ReviewProgressItem["jobStatus"],
  groups: SourceGroupType[],
  hasCategory: boolean,
): LiveHighlight {
  const failed = jobStatus === "FAILED" || stage === "FAILED";
  const completed = jobStatus === "COMPLETED" || stage === "COMPLETED";
  const intoGroupEdge = (groupType: SourceGroupType) =>
    hasCategory ? categoryToGroupEdgeId(groupType) : channelToGroupEdgeId(groupType);
  const pathStart = hasCategory
    ? [CHANNEL_NODE_ID, CATEGORY_NODE_ID]
    : [CHANNEL_NODE_ID];

  if (failed) {
    return { activeNodeIds: new Set(), activeEdgeIds: new Set(), decisionState: "failed" };
  }
  if (completed) {
    return { activeNodeIds: new Set(), activeEdgeIds: new Set(), decisionState: "success" };
  }

  switch (stage) {
    case "QUEUED":
    case "INDEXING":
      return {
        activeNodeIds: new Set([CHANNEL_NODE_ID]),
        activeEdgeIds: new Set(),
        decisionState: "pending",
      };
    case "PREPARING":
      return {
        activeNodeIds: new Set([...pathStart, ...groups.map(groupNodeId)]),
        activeEdgeIds: new Set([
          ...(hasCategory ? [CHANNEL_TO_CATEGORY_EDGE_ID] : []),
          ...groups.map(intoGroupEdge),
        ]),
        decisionState: "pending",
      };
    case "DETERMINISTIC_CHECKS": {
      const deterministic = groups.filter(
        (groupType) => groupType === "RULE_CUSTOM" || groupType === "DICTIONARY",
      );
      return {
        activeNodeIds: new Set([
          ...(hasCategory ? [CATEGORY_NODE_ID] : [CHANNEL_NODE_ID]),
          ...deterministic.map(groupNodeId),
        ]),
        activeEdgeIds: new Set(deterministic.map(intoGroupEdge)),
        decisionState: "pending",
      };
    }
    case "AI_REVIEW":
      return {
        activeNodeIds: new Set([...groups.map(groupNodeId), AI_REVIEW_NODE_ID]),
        activeEdgeIds: new Set(groups.map(groupToAiEdgeId)),
        decisionState: "pending",
      };
    case "FINALIZING":
      return {
        activeNodeIds: new Set([AI_REVIEW_NODE_ID, DECISION_NODE_ID]),
        activeEdgeIds: new Set([AI_TO_DECISION_EDGE_ID]),
        decisionState: "pending",
      };
    default:
      return { activeNodeIds: new Set(), activeEdgeIds: new Set(), decisionState: "pending" };
  }
}

/**
 * Groups that this run actually locked. The live graph still *renders* every
 * Sage Workflow group (including Educate Sage at 0) so the panel matches the
 * interactive guide; only these groups get flow/highlight.
 */
export function usedSourceGroups(counts: Record<SourceGroupType, number>): SourceGroupType[] {
  const used = GROUP_ORDER.filter((group) => counts[group] > 0);
  return used.length > 0 ? used : GROUP_ORDER;
}

export function sourceGroupType(
  source: ReviewProgressItem["sources"][number],
): SourceGroupType | null {
  if (source.citationType === "POLICY") return "POLICY";
  if (source.citationType === "DICTIONARY") return "DICTIONARY";
  if (source.citationType === "RULE") {
    return source.origin === "TAUGHT" ? "RULE_TAUGHT" : "RULE_CUSTOM";
  }
  return null;
}

/** Splits a review's locked sources into the same 4 visual groups as the Workflow tab. */
export function sourceGroupCounts(item: ReviewProgressItem): Record<SourceGroupType, number> {
  const counts: Record<SourceGroupType, number> = { POLICY: 0, RULE_CUSTOM: 0, RULE_TAUGHT: 0, DICTIONARY: 0 };
  for (const source of item.sources) {
    const group = sourceGroupType(source);
    if (group) counts[group] += 1;
  }
  return counts;
}
